/**
 * Notes API client: typed calls over the shell's office-scoped HTTP client,
 * validated with the module's contract schemas.
 */
import {
  AbbreviationListSchema,
  AbbreviationSchema,
  ArmedSessionSchema,
  StepUpFactorsSchema,
  ClinicalNoteSchema,
  ImportResultSchema,
  NoteContextSchema,
  NoteHistorySchema,
  NoteListSchema,
  NoteTemplateSchema,
  QueueSchema,
  TemplateListSchema,
  TemplateVersionsSchema,
  VisitDefaultsResultSchema,
  type CreateNoteRequest,
  type CreateTemplateRequest,
  type QueueTab,
  type SaveTemplateRequest,
  type StepUp,
  type TemplateCategory,
  type UpdateNoteRequest,
  VisitPrefillSchema,
  DictateResultSchema,
  NotesDaySchema,
  TodayNoteDraftSchema,
  TodayNoteFactsSchema,
  type TodayNoteDraftRequest,
} from '@velora/contracts/notes';
import type { HttpClient } from '@velora/sdk';
import { z } from 'zod';

const Ok = z.object({ ok: z.literal(true) });
const enc = encodeURIComponent;

/** Query keys start with the live entity so SSE `clinical_note` events refetch them. */
export const notesKeys = {
  patient: (patientId: string, q: string, status: string) =>
    ['clinical_note', 'patient', patientId, { q, status }] as const,
  note: (id: string) => ['clinical_note', 'one', id] as const,
  context: (patientId: string, appointmentId: string | null) =>
    ['clinical_note', 'context', patientId, appointmentId] as const,
  queue: (tab: QueueTab, provider: string, from: string | null, to: string | null) =>
    ['clinical_note', 'queue', { tab, provider, from, to }] as const,
  history: (id: string) => ['clinical_note', 'history', id] as const,
  templates: ['note_template', 'list'] as const,
  versions: (key: string) => ['note_template', 'versions', key] as const,
  abbreviations: ['note_template', 'abbreviations'] as const,
  /** Today's (or a day's) unsigned and missing notes per provider (W8). */
  day: (date: string | null) => ['clinical_note', 'day', date ?? 'today'] as const,
  todayFacts: (patientId: string, locationId: string, date: string, appointmentId: string | null) =>
    ['clinical_note', 'today-facts', patientId, locationId, date, appointmentId] as const,
};

export const notesApi = {
  list: (c: HttpClient, patientId: string, q: string, status: string, signal?: AbortSignal) =>
    c.request(
      `/notes/patients/${enc(patientId)}/notes?status=${enc(status)}${q ? `&q=${enc(q)}` : ''}&limit=200`,
      { schema: NoteListSchema, ...(signal ? { signal } : {}) },
    ),
  context: (c: HttpClient, patientId: string, appointmentId: string | null, signal?: AbortSignal) =>
    c.request(
      `/notes/patients/${enc(patientId)}/context${appointmentId ? `?appointmentId=${enc(appointmentId)}` : ''}`,
      { schema: NoteContextSchema, ...(signal ? { signal } : {}) },
    ),
  get: (c: HttpClient, id: string, signal?: AbortSignal) =>
    c.request(`/notes/notes/${enc(id)}`, {
      schema: ClinicalNoteSchema,
      ...(signal ? { signal } : {}),
    }),
  history: (c: HttpClient, id: string) =>
    c.request(`/notes/notes/${enc(id)}/history`, { schema: NoteHistorySchema }),
  /** Template chip values from today's visit (perio exam, recall, history review). */
  visitPrefill: (
    c: HttpClient,
    q: { patientId: string; locationId: string; date: string; appointmentId: string | null },
    signal?: AbortSignal,
  ) =>
    c.request(
      `/notes/notes/visit-prefill?patientId=${enc(q.patientId)}&locationId=${enc(q.locationId)}&date=${enc(q.date)}${q.appointmentId ? `&appointmentId=${enc(q.appointmentId)}` : ''}`,
      { schema: VisitPrefillSchema, ...(signal ? { signal } : {}) },
    ),
  create: (c: HttpClient, body: CreateNoteRequest) =>
    c.request('/notes/notes', { method: 'POST', body, schema: ClinicalNoteSchema }),
  update: (c: HttpClient, id: string, body: UpdateNoteRequest) =>
    c.request(`/notes/notes/${enc(id)}`, { method: 'PUT', body, schema: ClinicalNoteSchema }),
  sign: (c: HttpClient, id: string, expectedVersion: number, stepUp: StepUp, covering = false) =>
    c.request(`/notes/notes/${enc(id)}/sign`, {
      method: 'POST',
      body: { expectedVersion, stepUp, ...(covering ? { covering: true } : {}) },
      schema: ClinicalNoteSchema,
    }),
  requestSignature: (c: HttpClient, id: string) =>
    c.request(`/notes/notes/${enc(id)}/request-signature`, {
      method: 'POST',
      body: {},
      schema: ClinicalNoteSchema,
    }),
  addendum: (c: HttpClient, id: string, text: string, stepUp: StepUp) =>
    c.request(`/notes/notes/${enc(id)}/addenda`, {
      method: 'POST',
      body: { text, stepUp },
      schema: ClinicalNoteSchema,
    }),
  amend: (
    c: HttpClient,
    id: string,
    input: { struckText: string; replacement: string; reason: string },
    stepUp: StepUp,
  ) =>
    c.request(`/notes/notes/${enc(id)}/amendments`, {
      method: 'POST',
      body: { ...input, stepUp },
      schema: ClinicalNoteSchema,
    }),
  remove: (c: HttpClient, id: string, expectedVersion: number, reason: string) =>
    c.request(`/notes/notes/${enc(id)}/delete`, {
      method: 'POST',
      body: { expectedVersion, reason },
      schema: ClinicalNoteSchema,
    }),
  retryWriteBack: (c: HttpClient, id: string) =>
    c.request(`/notes/notes/${enc(id)}/writeback/retry`, {
      method: 'POST',
      schema: ClinicalNoteSchema,
    }),
  saveAsTemplate: (c: HttpClient, id: string, name: string, category: TemplateCategory) =>
    c.request(`/notes/notes/${enc(id)}/save-as-template`, {
      method: 'POST',
      body: { name, category },
      schema: NoteTemplateSchema,
    }),
  visitDefaults: (c: HttpClient, appointmentId: string) =>
    c.request('/notes/visit-defaults', {
      method: 'POST',
      body: { appointmentId },
      schema: VisitDefaultsResultSchema,
    }),
  queue: (
    c: HttpClient,
    tab: QueueTab,
    provider: string,
    from: string | null,
    to: string | null,
    signal?: AbortSignal,
  ) =>
    c.request(
      `/notes/queue?tab=${tab}&providerId=${enc(provider)}${from ? `&from=${from}` : ''}${to ? `&to=${to}` : ''}`,
      { schema: QueueSchema, ...(signal ? { signal } : {}) },
    ),
  arm: (c: HttpClient, stepUp: { pin: string } | { password: string }) =>
    c.request('/notes/sign-session', {
      method: 'POST',
      body: { stepUp },
      schema: ArmedSessionSchema,
    }),
  stepUpFactors: (c: HttpClient) =>
    c.request('/notes/sign-session', { schema: StepUpFactorsSchema }),
  disarm: (c: HttpClient) => c.request('/notes/sign-session', { method: 'DELETE', schema: Ok }),
  templates: (c: HttpClient, drafts: boolean, signal?: AbortSignal) =>
    c.request(`/notes/templates${drafts ? '?drafts=true' : ''}`, {
      schema: TemplateListSchema,
      ...(signal ? { signal } : {}),
    }),
  versions: (c: HttpClient, key: string) =>
    c.request(`/notes/templates/${enc(key)}/versions`, { schema: TemplateVersionsSchema }),
  createTemplate: (c: HttpClient, body: CreateTemplateRequest) =>
    c.request('/notes/templates', { method: 'POST', body, schema: NoteTemplateSchema }),
  saveTemplate: (c: HttpClient, key: string, body: SaveTemplateRequest) =>
    c.request(`/notes/templates/${enc(key)}`, { method: 'PUT', body, schema: NoteTemplateSchema }),
  publishTemplate: (c: HttpClient, key: string, changeNote?: string) =>
    c.request(`/notes/templates/${enc(key)}/publish`, {
      method: 'POST',
      body: changeNote ? { changeNote } : {},
      schema: NoteTemplateSchema,
    }),
  retireTemplate: (c: HttpClient, key: string) =>
    c.request(`/notes/templates/${enc(key)}`, { method: 'DELETE', schema: Ok }),
  favorite: (c: HttpClient, key: string, favorite: boolean) =>
    c.request(`/notes/templates/${enc(key)}/favorite`, {
      method: 'PUT',
      body: { favorite },
      schema: z.object({ favorite: z.boolean() }),
    }),
  importOd: (c: HttpClient, json: string, scope: 'user' | 'org') =>
    c.request('/notes/templates/import/od', {
      method: 'POST',
      body: { json, scope },
      schema: ImportResultSchema,
    }),
  abbreviations: (c: HttpClient, signal?: AbortSignal) =>
    c.request('/notes/abbreviations', {
      schema: AbbreviationListSchema,
      ...(signal ? { signal } : {}),
    }),
  saveAbbreviation: (c: HttpClient, abbr: string, expansion: string, scope: 'user' | 'org') =>
    c.request('/notes/abbreviations', {
      method: 'POST',
      body: { abbr, expansion, scope },
      schema: AbbreviationSchema,
    }),
  /** What the AI may use for today's note (W8): the visit's record facts. */
  todayFacts: (
    c: HttpClient,
    q: { patientId: string; locationId: string; date: string; appointmentId: string | null },
    signal?: AbortSignal,
  ) =>
    c.request(
      `/notes/today-note/facts?patientId=${enc(q.patientId)}&locationId=${enc(q.locationId)}&date=${enc(q.date)}${q.appointmentId ? `&appointmentId=${enc(q.appointmentId)}` : ''}`,
      { schema: TodayNoteFactsSchema, ...(signal ? { signal } : {}) },
    ),
  /** A suggested draft (never saved or signed by the server). */
  todayDraft: (c: HttpClient, body: TodayNoteDraftRequest) =>
    c.request('/notes/today-note/draft', { method: 'POST', body, schema: TodayNoteDraftSchema }),
  /** One dictation chunk, transcribed on C2G's own speech server (not stored). */
  dictate: (
    c: HttpClient,
    body: { pcm16Base64: string; sampleRateHz: number; locationId: string },
  ) => c.request('/notes/dictate', { method: 'POST', body, schema: DictateResultSchema }),
  day: (c: HttpClient, date: string | null, signal?: AbortSignal) =>
    c.request(`/notes/day${date ? `?date=${enc(date)}` : ''}`, {
      schema: NotesDaySchema,
      ...(signal ? { signal } : {}),
    }),
  deleteAbbreviation: (c: HttpClient, id: string) =>
    c.request(`/notes/abbreviations/${enc(id)}`, { method: 'DELETE', schema: Ok }),
};

/** Error details from the API envelope (409 conflict, blockers, reasons). */
export function errorDetails(
  err: unknown,
): { status: number; code: string; message: string; details: Record<string, unknown> } | null {
  if (!err || typeof err !== 'object') return null;
  const e = err as { status?: number; body?: unknown; message?: string };
  const body = e.body as
    { error?: { code?: string; message?: string; details?: unknown } } | undefined;
  if (typeof e.status !== 'number') return null;
  return {
    status: e.status,
    code: body?.error?.code ?? 'error',
    message: body?.error?.message ?? e.message ?? 'Something went wrong.',
    details: (body?.error?.details as Record<string, unknown> | undefined) ?? {},
  };
}
